/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode, useRef } from 'react';

import { css as customCss } from '@compiled/react';

import { cssMap, cx, jsx } from '@atlaskit/css';
/* eslint-disable @repo/internal/react/require-jsdoc */
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { BrandHeading } from '../brand-heading';
import { HeroGridBackground } from '../hero/grid-background';
import { Image } from '../image';
import { PageContentContainer } from '../page-content-container';
import { RelativePosition } from '../relative-position';

const styles = cssMap({
	bannerContainer: {
		display: 'flex',
		width: '100%',
		justifyContent: 'center',
	},
	backgroundColor: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	bannerWidth: {
		width: '100%',
		maxWidth: '980px',
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: token('space.200'),
		paddingBlock: token('space.500'),
		'@media (min-width: 30rem)': {
			flexDirection: 'row',
			alignItems: 'center',
			paddingBlock: token('space.500'),
		},
	},
	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		height: '100%',
		flex: 3,
		width: '100%',
		'@media (min-width: 30rem)': {
			minHeight: '220px',
		},
	},
	imageContainer: {
		flex: 2,
		width: '100%',
		maxWidth: '400px',
	},
	edgeToEdgeWrapper: {
		position: 'absolute',
		zIndex: -1,
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: '50%',
		width: '100vw',
		pointerEvents: 'none',
	},
	eyebrow: {
		font: token('font.code'),
		color: token('color.text.subtle'),
	},
});

const customEdgeToEdgeWrapperStyles = customCss({
	insetInlineEnd: 'auto',
	marginInlineStart: '-50vw',
});

export const Banner = ({
	title,
	eyebrow,
	description,
	image,
	hasBackgroundColor,
	hasDotGrid = false,
}: {
	title: string;
	/**
	 * Optional label rendered above the title. Styled using the `font.code` token
	 * in uppercase (the "Code" typography token, all caps).
	 */
	eyebrow?: string;
	description: string;
	image?: {
		light: string;
		dark: string;
	};
	hasBackgroundColor?: boolean;
	hasDotGrid?: boolean;
}): React.JSX.Element => {
	const { colorMode = 'light' } = useThemeObserver();
	const src = colorMode === 'dark' ? image?.dark || '' : image?.light || '';
	const containerRef = useRef<HTMLDivElement>(null);

	const content = (
		<Box xcss={styles.bannerWidth}>
			<Box xcss={styles.contentContainer}>
				<Stack space="space.100">
					{eyebrow && (
						<Box as="div" xcss={styles.eyebrow}>
							{eyebrow.toUpperCase()}
						</Box>
					)}
					<BrandHeading as="h1" size="xxlarge">
						{title}
					</BrandHeading>
					<Text size="large">{description}</Text>
				</Stack>
			</Box>
			{image && (
				<ImageContainer>
					<Image src={src} alt={`Banner for ${title} page`} />
				</ImageContainer>
			)}
		</Box>
	);

	return (
		<Box xcss={cx(styles.bannerContainer, hasBackgroundColor && styles.backgroundColor)}>
			{hasDotGrid ? (
				<RelativePosition>
					<div ref={containerRef} css={[styles.edgeToEdgeWrapper, customEdgeToEdgeWrapperStyles]}>
						<HeroGridBackground containerRef={containerRef} />
					</div>
					<PageContentContainer>{content}</PageContentContainer>
				</RelativePosition>
			) : (
				<PageContentContainer>{content}</PageContentContainer>
			)}
		</Box>
	);
};

const ImageContainer = ({ children }: { children: ReactNode }) => {
	return <Box xcss={styles.imageContainer}>{children}</Box>;
};
